import { defineStore } from 'pinia'
import { useSupabaseClient, useRoute } from '#imports'

interface Database {
  public: {
    Tables: {
      plans: {
        Row: PlanType
        Insert: PlanType
        Update: Partial<PlanType>
      }
      profiles: {
        Row: ProfileType
        Insert: ProfileType
        Update: Partial<ProfileType>
      }
      payments: {
        Row: PaymentType
        Insert: PaymentType
        Update: Partial<PaymentType>
      }
      plan_history: {
        Row: PlanHistoryType
        Insert: PlanHistoryType
        Update: Partial<PlanHistoryType>
      }
    }
  }
}

interface PlanType {
  id: string
  name: string
  description: string
  price_monthly: number
  max_contacts: number
  max_campaigns: number
  max_domains: number
  is_trial: boolean
  active: boolean
  stripe_product_id?: string
  stripe_price_id?: string
}

interface ProfileType {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  plan_id?: string
  plan_start_date?: string
  plan_end_date?: string
  stripe_subscription_id?: string
  created_at: string
  updated_at: string
  plans?: PlanType
}

interface PaymentType {
  id: string
  user_id: string
  amount: number
  payment_date: string
  status: string
  plans: PlanType
}

interface PlanHistoryType {
  id: string
  user_id: string
  change_date: string
  change_type: string
  previous_plan: PlanType
  new_plan: PlanType
}

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    plans: [] as PlanType[],
    currentPlan: null as PlanType | null,
    planDetails: null as ProfileType | null,
    paymentHistory: [] as PaymentType[],
    planHistory: [] as PlanHistoryType[],
    loading: false,
    error: null as string | null
  }),

  getters: {
    isTrialPlan: (state) => state.currentPlan?.is_trial || false,
    trialDaysRemaining: (state) => {
      if (!state.isTrialPlan || !state.planDetails?.plan_end_date) return 0
      const endDate = new Date(state.planDetails.plan_end_date)
      const now = new Date()
      const diff = endDate.getTime() - now.getTime()
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
    }
  },

  actions: {
    // Format price from cents to dollars
    formatPrice(cents: number) {
      if (!cents && cents !== 0) return '$0'
      return `$${(cents / 100).toFixed(2)}`
    },

    // Format date to local string
    formatDate(date: string) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    // Get remaining time on current plan
    getPlanTimeRemaining() {
      if (!this.planDetails?.plan_end_date) return 'No end date'
      const endDate = new Date(this.planDetails.plan_end_date)
      const now = new Date()
      if (endDate < now) return 'Expired'
      return `Renews ${this.formatDate(this.planDetails.plan_end_date)}`
    },

    // Fetch all subscription-related data
    async fetchSubscriptionData() {
      try {
        this.loading = true
        this.error = null

        const supabase = useSupabaseClient<Database>()

        // Get all active plans
        const { data: plansData, error: plansError } = await supabase
          .from('plans')
          .select('*')
          .eq('active', true)
          .order('price_monthly')

        if (plansError) throw plansError
        this.plans = plansData

        // Get authenticated user
        const { data: { user }, error: userError } = await supabase.auth.getUser()
        if (userError) throw userError
        if (!user) throw new Error('User not found')

        // Get current user's profile and plan details
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*, plans(*)')
          .eq('id', user.id)
          .single()

        if (profileError) throw profileError
        this.currentPlan = profile?.plans || null
        this.planDetails = profile

        // Get payment history
        const { data: payments, error: paymentsError } = await supabase
          .from('payments')
          .select('*, plans(*)')
          .eq('user_id', user.id)
          .order('payment_date', { ascending: false })

        if (paymentsError) throw paymentsError
        this.paymentHistory = payments

        // Get plan history
        const { data: history, error: historyError } = await supabase
          .from('plan_history')
          .select('*, previous_plan:plans!plan_history_previous_plan_id_fkey(*), new_plan:plans!plan_history_new_plan_id_fkey(*)')
          .eq('user_id', user.id)
          .order('change_date', { ascending: false })

        if (historyError) throw historyError
        this.planHistory = history

      } catch (err: any) {
        this.error = err.message
        console.error('Error fetching subscription data:', err)
      } finally {
        this.loading = false
      }
    },

    // Start Stripe checkout process
    async startCheckout(planId: string) {
      try {
        const response = await $fetch('/api/subscription/checkout', {
          method: 'POST',
          body: { planId }
        }) as { url: string }

        if (response.url) {
          window.location.href = response.url
        }
      } catch (err: any) {
        this.error = err.message
        console.error('Error starting checkout:', err)
      }
    },

    // Cancel current subscription
    async cancelSubscription() {
      try {
        await $fetch('/api/subscription/cancel', {
          method: 'POST'
        })
        await this.fetchSubscriptionData()
        return true
      } catch (err: any) {
        this.error = err.message
        console.error('Error canceling subscription:', err)
        return false
      }
    },

    // Check checkout status from URL parameters
    checkCheckoutStatus() {
      const route = useRoute()
      // Handle SSR case where route.query might be undefined
      const query = route.query || {}
      
      return {
        isSuccess: query.success === 'true',
        isCanceled: query.canceled === 'true',
        isDemo: query.demo === 'true',
        planName: (query.plan as string) || undefined
      }
    }
  },

  persist: true
}) 